/**
 * cart-billing-form.js
 * Toggle Privato/Azienda, validazione, save via cart attributes (/cart/update.js)
 * Sync bidirezionale cart page ↔ cart drawer via sessionStorage
 */

(function () {
  'use strict';

  // Guard: evita doppia esecuzione se lo script viene incluso
  // sia dalla pagina carrello che dal drawer snippet.
  if (window.__thbCartBillingLoaded) return;
  window.__thbCartBillingLoaded = true;

  const STORAGE_KEY = 'thb_billing_data';

  // Etichette a schermo, passate dal template nella lingua attiva.
  // Le chiavi degli attributi carrello restano italiane: sono dati, non testo.
  // Lette a ogni chiamata: questo script può girare prima dello snippet che
  // definisce window.thbBillingI18n.
  function t(key, fallback) {
    const i18n = window.thbBillingI18n;
    return (i18n && i18n[key]) || fallback;
  }
  function fieldLabel(key) {
    const i18n = window.thbBillingI18n;
    return (i18n && i18n.fieldLabels && i18n.fieldLabels[key]) || key;
  }

  // Tutte le chiavi billing che gestiamo — usate per pulizia selettiva
  const BILLING_ATTR_KEYS = [
    'Fattura',
    'Tipo soggetto',
    'Codice Fiscale',
    'Partita IVA',
    'Codice SDI',
    'PEC',
  ];

  // Solo i campi compilabili (senza i due attributi di stato).
  const BILLING_FIELD_KEYS = ['Codice Fiscale', 'Partita IVA', 'Codice SDI', 'PEC'];

  /* ------------------------------------------------------------------ */
  /*  Validazione                                                         */
  /* ------------------------------------------------------------------ */

  function normalizeCF(v) {
    return v.trim().toUpperCase().replace(/\s+/g, '');
  }
  function validateCF(v) {
    return /^[A-Z0-9]{16}$/.test(normalizeCF(v));
  }
  function validatePIVA(v) {
    return /^\d{11}$/.test(v.trim());
  }
  function validateEmail(v) {
    return v.trim() === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
  }

  function setInputState(input, state) {
    input.classList.toggle('is-valid',   state === 'valid');
    input.classList.toggle('is-invalid', state === 'invalid');
  }

  function showError(errEl, show) {
    if (!errEl) return;
    errEl.classList.toggle('is-visible', show);
  }

  /* ------------------------------------------------------------------ */
  /*  Cart attributes API                                                 */
  /* ------------------------------------------------------------------ */

  async function getCartAttributes() {
    const res = await fetch('/cart.js');
    const cart = await res.json();
    return cart.attributes || {};
  }

  // NB Shopify: /cart/update.js fa il MERGE degli attributi passati, non li
  // sostituisce. Un attributo si rimuove solo inviandolo come stringa vuota:
  // ometterlo (o fare delete sull'oggetto) lo lascia intatto nel carrello.
  // Passiamo quindi sempre tutte le nostre chiavi, azzerando quelle inutilizzate,
  // e non tocchiamo gli attributi di terzi.
  async function updateCartAttributes(type, fields) {
    const billing = {
      'Fattura': 'Richiesta',
      'Tipo soggetto': type === 'azienda' ? 'Azienda' : 'Privato',
      ...fields,
    };
    BILLING_ATTR_KEYS.forEach((k) => {
      if (!(k in billing)) billing[k] = '';
    });

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: billing }),
    });
  }

  async function clearCartAttributes() {
    const cleared = {};
    BILLING_ATTR_KEYS.forEach((k) => { cleared[k] = ''; });
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: cleared }),
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Helpers UI                                                          */
  /* ------------------------------------------------------------------ */

  function getActiveType(ctx) {
    const activeBtn = document.querySelector(`.billing-type-btn--active[data-ctx="${ctx}"]`);
    return activeBtn ? activeBtn.dataset.type : 'privato';
  }

  function switchType(ctx, type) {
    document.querySelectorAll(`.billing-type-btn[data-ctx="${ctx}"]`).forEach((btn) => {
      btn.classList.toggle('billing-type-btn--active', btn.dataset.type === type);
    });
    const privato = document.getElementById(`billing-fields-privato--${ctx}`);
    const azienda = document.getElementById(`billing-fields-azienda--${ctx}`);
    if (privato) privato.style.display = type === 'privato' ? '' : 'none';
    if (azienda) azienda.style.display = type === 'azienda' ? '' : 'none';
  }

  function collectFields(ctx, type) {
    const container = document.getElementById(`billing-fields-${type}--${ctx}`);
    if (!container) return {};
    const fields = {};
    container.querySelectorAll('.billing-input').forEach((input) => {
      const key = input.dataset.billingKey;
      const val = input.value.trim();
      if (key && val) {
        fields[key] = key === 'Codice Fiscale' ? normalizeCF(val) : val;
      }
    });
    return fields;
  }

  function fillFields(ctx, type, fields) {
    for (const [key, val] of Object.entries(fields)) {
      const input = document.querySelector(
        `[data-billing-key="${key}"][data-billing-ctx="${ctx}"]`
      );
      if (input && val) {
        input.value = val;
        setInputState(input, 'valid');
      }
    }
  }

  function showSavedCard(ctx, type, fields) {
    const card = document.getElementById(`billing-saved--${ctx}`);
    const text = document.getElementById(`billing-saved-text--${ctx}`);
    if (!card || !text) return;
    const label = type === 'azienda' ? t('company', 'Azienda') : t('individual', 'Privato');
    let html = `<strong>${label}</strong>`;
    for (const [k, v] of Object.entries(fields)) {
      if (v) html += `${fieldLabel(k)}: <b>${v}</b><br>`;
    }
    text.innerHTML = html;
    card.classList.add('is-visible');
  }

  function hideSavedCard(ctx) {
    const card = document.getElementById(`billing-saved--${ctx}`);
    if (card) card.classList.remove('is-visible');
  }

  /* ------------------------------------------------------------------ */
  /*  Validazione completa al submit                                      */
  /* ------------------------------------------------------------------ */

  function validateAll(ctx, type) {
    let ok = true;

    if (type === 'privato') {
      const cfInput = document.getElementById(`billing-cf--${ctx}`);
      const errEl   = document.getElementById(`err-cf--${ctx}`);
      if (cfInput) {
        const valid = cfInput.value.trim() !== '' && validateCF(cfInput.value);
        setInputState(cfInput, valid ? 'valid' : 'invalid');
        showError(errEl, !valid);
        if (!valid) ok = false;
      }
    } else {
      const pivaInput = document.getElementById(`billing-piva--${ctx}`);
      const errPiva   = document.getElementById(`err-piva--${ctx}`);
      if (pivaInput) {
        const valid = pivaInput.value.trim() !== '' && validatePIVA(pivaInput.value);
        setInputState(pivaInput, valid ? 'valid' : 'invalid');
        showError(errPiva, !valid);
        if (!valid) ok = false;
      }
      const sdiInput = document.getElementById(`billing-sdi--${ctx}`);
      const pecInput = document.getElementById(`billing-pec--${ctx}`);
      const errPec   = document.getElementById(`err-pec--${ctx}`);
      if (sdiInput && pecInput) {
        const hasSdi = sdiInput.value.trim() !== '';
        const hasPec = pecInput.value.trim() !== '';
        if (!hasSdi && !hasPec) {
          setInputState(pecInput, 'invalid');
          if (errPec) { errPec.textContent = t('sdiOrPecError', 'Inserire SDI oppure PEC'); showError(errPec, true); }
          ok = false;
        } else if (hasPec && !validateEmail(pecInput.value)) {
          setInputState(pecInput, 'invalid');
          if (errPec) { errPec.textContent = t('pecError', 'Indirizzo PEC non valido'); showError(errPec, true); }
          ok = false;
        } else {
          setInputState(pecInput, hasPec ? 'valid' : '');
          showError(errPec, false);
        }
      }
    }

    return ok;
  }

  /* ------------------------------------------------------------------ */
  /*  Check silenzioso — senza side-effect UI                             */
  /*  Ritorna true solo se i campi obbligatori del tab attivo            */
  /*  risultano validi secondo le stesse regole di validateAll().        */
  /* ------------------------------------------------------------------ */

  function isFormValid(ctx, type) {
    if (type === 'privato') {
      const cfInput = document.getElementById(`billing-cf--${ctx}`);
      if (!cfInput) return false;
      return cfInput.value.trim() !== '' && validateCF(cfInput.value);
    }
    // azienda
    const pivaInput = document.getElementById(`billing-piva--${ctx}`);
    const sdiInput  = document.getElementById(`billing-sdi--${ctx}`);
    const pecInput  = document.getElementById(`billing-pec--${ctx}`);
    if (!pivaInput || !sdiInput || !pecInput) return false;

    const pivaOk = pivaInput.value.trim() !== '' && validatePIVA(pivaInput.value);
    if (!pivaOk) return false;

    const hasSdi = sdiInput.value.trim() !== '';
    const hasPec = pecInput.value.trim() !== '';
    if (!hasSdi && !hasPec) return false;
    if (hasPec && !validateEmail(pecInput.value)) return false;

    return true;
  }

  /* ------------------------------------------------------------------ */
  /*  Stato "dati salvati": verifica attributi del carrello               */
  /* ------------------------------------------------------------------ */

  // Gli attributi del carrello sono la fonte autorevole: sopravvivono a reload,
  // re-render della sezione, nuove schede e riapertura del browser, mentre
  // sessionStorage no. Qui li leggiamo una volta e li teniamo in cache.
  // Ritorna { type, fields } se ci sono dati salvati, altrimenti false.
  let _savedCache = null;

  async function loadSavedBilling() {
    if (_savedCache !== null) return _savedCache;
    try {
      const attrs = await getCartAttributes();
      if (attrs && attrs['Fattura'] === 'Richiesta' && attrs['Tipo soggetto']) {
        const fields = {};
        BILLING_FIELD_KEYS.forEach((k) => {
          if (attrs[k]) fields[k] = attrs[k];
        });
        _savedCache = {
          type: attrs['Tipo soggetto'] === 'Azienda' ? 'azienda' : 'privato',
          fields,
        };
      } else {
        _savedCache = false;
      }
    } catch (e) {
      _savedCache = false;
    }
    return _savedCache;
  }
  function invalidateSavedCache() { _savedCache = null; }

  /* ------------------------------------------------------------------ */
  /*  Idratazione del form dai dati salvati                               */
  /*                                                                      */
  /*  Il tema rirenderizza la sezione carrello (innerHTML) a ogni cambio  */
  /*  quantita' o rimozione riga: gli input tornano vuoti perche' il      */
  /*  markup non porta i value lato server. Senza reidratazione il gate   */
  /*  vede un form vuoto pur avendo i dati nel carrello e blocca il       */
  /*  checkout con "dati non validi".                                     */
  /* ------------------------------------------------------------------ */

  function isFormEmpty(ctx, type) {
    return Object.keys(collectFields(ctx, type)).length === 0;
  }

  function applySavedToForm(ctx, saved) {
    if (!document.getElementById(`billing-form--${ctx}`)) return;
    switchType(ctx, saved.type);
    fillFields(ctx, saved.type, saved.fields);
    showSavedCard(ctx, saved.type, saved.fields);
    const clearBtn = document.querySelector(`.billing-clear-btn[data-ctx="${ctx}"]`);
    if (clearBtn) clearBtn.classList.add('is-visible');
  }

  /* ------------------------------------------------------------------ */
  /*  Gate del bottone checkout                                           */
  /* ------------------------------------------------------------------ */

  const checkoutMessage = {
    get defaultCart()   { return t('checkoutBlockedCart', 'Compila e salva i dati di fatturazione per procedere al checkout'); },
    get defaultDrawer() { return t('checkoutBlockedDrawer', 'Compila i dati di fatturazione nel carrello per procedere'); },
    get invalid()       { return t('checkoutInvalid', 'I dati di fatturazione inseriti non sono validi'); },
    get unsaved()       { return t('checkoutUnsaved', 'Clicca "Salva dati fatturazione" prima di procedere'); },
  };

  function setCheckoutState(ctx, { enabled, message }) {
    const btn = document.querySelector(`[data-billing-checkout][data-billing-ctx="${ctx}"]`);
    const errBox = document.querySelector(`[data-billing-checkout-error][data-billing-ctx="${ctx}"]`);
    if (!btn) return;

    btn.disabled = !enabled;
    btn.classList.toggle('is-disabled-billing', !enabled);
    btn.setAttribute('aria-disabled', String(!enabled));

    if (errBox) {
      const txt = errBox.querySelector('[data-billing-checkout-error-text]');
      if (!enabled) {
        if (txt && message) txt.textContent = message;
        errBox.hidden = false;
      } else {
        errBox.hidden = true;
      }
    }
  }

  async function refreshCheckoutGate(ctx) {
    const hasForm = !!document.getElementById(`billing-form--${ctx}`);
    const saved   = await loadSavedBilling();

    // Se il form non esiste in questo contesto (es. drawer senza form):
    // il gate dipende solo dai dati salvati.
    if (!hasForm) {
      setCheckoutState(ctx, {
        enabled: !!saved,
        message: ctx === 'drawer' ? checkoutMessage.defaultDrawer : checkoutMessage.defaultCart,
      });
      return;
    }

    if (saved && isFormEmpty(ctx, saved.type)) {
      // Rete di sicurezza: form svuotato da un re-render (o aperto in una
      // scheda nuova, dove sessionStorage e' vuoto). Lo ripopoliamo dai
      // dati salvati nel carrello invece di bloccare il checkout.
      applySavedToForm(ctx, saved);
    }

    const type = getActiveType(ctx);
    const formValid = isFormValid(ctx, type);

    if (saved) {
      // Se l'utente ha salvato MA poi ha modificato i campi rendendoli
      // invalidi, o ha sporcato il form senza risalvare, riblocchiamo.
      const dirty = isFormDirtyVsSaved(ctx, saved);
      if (dirty && !formValid) {
        const message = isFormEmpty(ctx, type)
          ? (ctx === 'drawer' ? checkoutMessage.defaultDrawer : checkoutMessage.defaultCart)
          : checkoutMessage.invalid;
        setCheckoutState(ctx, { enabled: false, message });
        return;
      }
      if (dirty && formValid) {
        setCheckoutState(ctx, { enabled: false, message: checkoutMessage.unsaved });
        return;
      }
      setCheckoutState(ctx, { enabled: true });
      return;
    }

    // Non salvato
    setCheckoutState(ctx, {
      enabled: false,
      message: ctx === 'drawer' ? checkoutMessage.defaultDrawer : checkoutMessage.defaultCart,
    });
  }

  function refreshAllCheckoutGates() {
    ['cart', 'drawer'].forEach((ctx) => {
      if (document.querySelector(`[data-billing-checkout][data-billing-ctx="${ctx}"]`)) {
        refreshCheckoutGate(ctx);
      }
    });
  }

  /* Rileva se il form attuale diverge dai dati salvati nel carrello.
     Il confronto usa gli attributi del carrello, non sessionStorage: quello
     e' per-scheda e sparisce, gli attributi no. */
  function isFormDirtyVsSaved(ctx, saved) {
    if (!saved) return true;
    const currentType = getActiveType(ctx);
    if (currentType !== saved.type) return true;
    const currentFields = collectFields(ctx, currentType);
    const savedFields = saved.fields || {};
    const keys = new Set([...Object.keys(savedFields), ...Object.keys(currentFields)]);
    for (const k of keys) {
      if ((savedFields[k] || '') !== (currentFields[k] || '')) return true;
    }
    return false;
  }

  /* ------------------------------------------------------------------ */
  /*  Sync altri contesti                                                 */
  /* ------------------------------------------------------------------ */

  function syncOtherContexts(sourceCtx, type, fields) {
    ['cart', 'drawer'].forEach((ctx) => {
      if (ctx === sourceCtx) return;
      if (!document.getElementById(`billing-form--${ctx}`)) return;
      switchType(ctx, type);
      fillFields(ctx, type, fields);
      showSavedCard(ctx, type, fields);
      const cb = document.querySelector(`.billing-clear-btn[data-ctx="${ctx}"]`);
      if (cb) cb.classList.add('is-visible');
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Init per ogni contesto                                              */
  /* ------------------------------------------------------------------ */

  function initContext(ctx) {
    const root = document.getElementById(`billing-form--${ctx}`);
    if (!root) return;
    // Guard: se il root è già stato inizializzato, non raddoppiamo i listener.
    if (root.dataset.billingInited === '1') return;
    root.dataset.billingInited = '1';

    /* Toggle tipo */
    document.querySelectorAll(`.billing-type-btn[data-ctx="${ctx}"]`).forEach((btn) => {
      btn.addEventListener('click', () => {
        switchType(ctx, btn.dataset.type);
        refreshCheckoutGate(ctx);
      });
    });

    /* Validazione blur + refresh gate */
    document.querySelectorAll(`[data-billing-ctx="${ctx}"]`).forEach((input) => {
      input.addEventListener('blur', () => {
        const key = input.dataset.billingKey;
        if (!key || !input.value.trim()) {
          refreshCheckoutGate(ctx);
          return;
        }
        let valid = true;
        if (key === 'Codice Fiscale') valid = validateCF(input.value);
        if (key === 'Partita IVA')    valid = validatePIVA(input.value);
        if (key === 'PEC')            valid = validateEmail(input.value);
        setInputState(input, valid ? 'valid' : 'invalid');
        refreshCheckoutGate(ctx);
      });
      // refresh "morbido" anche durante la digitazione
      input.addEventListener('input', () => refreshCheckoutGate(ctx));
    });

    /* Save */
    const saveBtn = document.querySelector(`.billing-save-btn[data-ctx="${ctx}"]`);
    if (saveBtn) {
      saveBtn.addEventListener('click', async () => {
        const type = getActiveType(ctx);
        if (!validateAll(ctx, type)) return;

        const fields = collectFields(ctx, type);
        const span = saveBtn.querySelector('span');
        saveBtn.disabled = true;
        if (span) span.textContent = t('saving', 'Salvataggio…');

        try {
          await updateCartAttributes(type, fields);
          invalidateSavedCache();
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ type, fields }));
          showSavedCard(ctx, type, fields);
          const clearBtn = document.querySelector(`.billing-clear-btn[data-ctx="${ctx}"]`);
          if (clearBtn) clearBtn.classList.add('is-visible');
          syncOtherContexts(ctx, type, fields);
          refreshAllCheckoutGates();
        } catch (e) {
          console.error('[billing-form] Errore salvataggio attributi:', e);
          alert(t('saveError', 'Errore nel salvataggio. Riprova.'));
        } finally {
          saveBtn.disabled = false;
          if (span) span.textContent = t('save', 'Salva dati fatturazione');
        }
      });
    }

    /* Clear */
    const clearBtn = document.querySelector(`.billing-clear-btn[data-ctx="${ctx}"]`);
    if (clearBtn) {
      clearBtn.addEventListener('click', async () => {
        await clearCartAttributes();
        invalidateSavedCache();
        sessionStorage.removeItem(STORAGE_KEY);
        ['cart', 'drawer'].forEach((c) => {
          if (!document.getElementById(`billing-form--${c}`)) return;
          switchType(c, 'privato');
          document.querySelectorAll(`[data-billing-ctx="${c}"]`).forEach((input) => {
            input.value = '';
            setInputState(input, '');
          });
          hideSavedCard(c);
          const cb = document.querySelector(`.billing-clear-btn[data-ctx="${c}"]`);
          if (cb) cb.classList.remove('is-visible');
        });
        refreshAllCheckoutGates();
      });
    }

    /* Restore: prima sessionStorage (istantaneo), poi gli attributi del
       carrello come fonte autorevole. Il secondo copre i casi in cui
       sessionStorage non c'e': scheda nuova, sessione riaperta, oppure
       sezione rirenderizzata dal tema. */
    const cached = sessionStorage.getItem(STORAGE_KEY);
    if (cached) {
      try {
        const { type, fields } = JSON.parse(cached);
        applySavedToForm(ctx, { type, fields });
      } catch (e) {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    }

    loadSavedBilling().then((saved) => {
      if (!saved) return;
      if (isFormEmpty(ctx, saved.type)) {
        applySavedToForm(ctx, saved);
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
      }
      refreshCheckoutGate(ctx);
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Gate: intercetta click sul bottone checkout disabilitato            */
  /*  (safeguard: oltre all'attributo disabled)                          */
  /*                                                                      */
  /*  Uso delegazione globale perché il drawer e il carrello vengono     */
  /*  re-renderizzati (innerHTML) dopo ogni update: gli handler diretti  */
  /*  sui bottoni andrebbero persi.                                       */
  /* ------------------------------------------------------------------ */

  let _globalGateBound = false;

  function initGlobalGate() {
    if (_globalGateBound) return;
    _globalGateBound = true;

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-billing-checkout]');
      if (!btn) return;
      if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') {
        e.preventDefault();
        e.stopImmediatePropagation();
        const ctx = btn.dataset.billingCtx || 'cart';
        const form = document.getElementById(`billing-form--${ctx}`);
        if (form && typeof form.scrollIntoView === 'function') {
          const details = document.getElementById(`billing-details--${ctx}`);
          if (details && !details.open) details.open = true;
          form.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (ctx === 'drawer') {
          // Nel drawer non c'è il form: suggerisci di aprire il carrello.
          const cartLink = document.querySelector('.cart-drawer-buttons a[href*="/cart"]');
          if (cartLink) cartLink.focus();
        }
        return false;
      }
    }, true); // capture: intercetta prima dei submit listener nativi
  }

  /* ------------------------------------------------------------------ */
  /*  Bootstrap                                                           */
  /* ------------------------------------------------------------------ */

  function init() {
    initGlobalGate();
    initContext('cart');
    initContext('drawer');
    refreshAllCheckoutGates();
  }

  // Dopo un re-render il root del form e' un nodo nuovo, senza il flag
  // billingInited: initContext ripassa e riaggancia i listener (salva,
  // cancella, toggle, blur/input) che altrimenti resterebbero sul DOM morto.
  function reinitAll() {
    initContext('cart');
    initContext('drawer');
    refreshAllCheckoutGates();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('cart:open', () => {
    initContext('drawer');
    refreshAllCheckoutGates();
  });

  // Eventi emessi dal tema quando cambia una riga del carrello: il markup
  // della sezione e' gia' stato sostituito quando arrivano.
  document.addEventListener('line-item:change:end', () => {
    invalidateSavedCache();
    reinitAll();
  });
  document.addEventListener('cart:item-added', () => {
    invalidateSavedCache();
    reinitAll();
  });

  // Dopo ogni update del carrello (quantità, rimozione item, ecc.) il tema
  // rigenera l'HTML: invalidiamo la cache e riverifichiamo lo stato.
  document.addEventListener('cart:updated', () => {
    invalidateSavedCache();
    reinitAll();
  });
  document.addEventListener('cart:refresh', () => {
    invalidateSavedCache();
    // Dopo il re-render il DOM cambia: re-init contesti
    setTimeout(reinitAll, 50);
  });

  // MutationObserver come rete di sicurezza: ogni volta che un bottone
  // data-billing-checkout appare nel DOM (es. dopo re-render drawer),
  // riapplichiamo lo stato corrente.
  const WATCHED = '[data-billing-checkout], .billing-form-body';
  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        const hit = (node.matches && node.matches(WATCHED)) ||
                    (node.querySelector && node.querySelector(WATCHED));
        if (hit) {
          // Il carrello e' stato rirenderizzato: la cache degli attributi
          // puo' essere stantia e i listener vanno riagganciati.
          invalidateSavedCache();
          reinitAll();
          return;
        }
      }
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });

})();